import React, { useState, useMemo } from "react";
import PageLayout from "./ui/PageLayout";
import StatusCards from "./ui/StatusCards";
import StatusCardsSkeleton from "./ui/StatusCardsSkeleton";
import {
  DUMMY_CARD_DATA,
  STATUS_TO_TYPE_MAPPER,
} from "../constants/OutreachedJobs";
import CustomTable from "./ui/DataTable";
import PageCard from "./ui/PageCard";
import Pagination from "./ui/Pagination";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCustomPagination,
  setCustomPagination,
} from "../features/paginationStore";
import { selectUuid } from "../features/userid";
import Tag from "./ui/Tag";
import Input from "./ui/Input";
import { CiSearch } from "react-icons/ci";
import Select from "./ui/Select";
import Copyright from "./ui/Copyright";
import TableRedirectionLink from "./ui/TableRedirectionLink";
import { REDIRECTION_LINKS } from "../constants/common";
import useFetch from "../hooks/useFetch";
import { TailSpin } from "react-loader-spinner";
import { selectCid } from "../features/companyid";
import { getSpecialTab, getUserName, getUserEmail } from "../features/userid";
import EmptyScreen from "./ui/EmptyScreen";
import Button from "./ui/Button";
import { formatDateToShortMonth } from "../utils/dateUtils";
import OutReachedAction from "./outReachedAction";
import { sendUpgradeEmail } from "../utils/sendUpgradeEmail";
import { toast } from "react-toastify";

const UpgradeBanner = () => {
  const userName = useSelector(getUserName);
  const userEmail = useSelector(getUserEmail);
  const companyId = useSelector(selectCid);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleContact = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await sendUpgradeEmail({ userName, companyId, userEmail });
      setSent(true);
      toast.success("Email sent! Our team will reach out to you shortly.");
    } catch (err) {
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl p-[2px] bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] mb-6">
      <div className="bg-white rounded-[10px] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.4372 10.0828L11.4075 8.59375L9.9231 4.56094C9.83519 4.32213 9.67616 4.11604 9.46744 3.97046C9.25873 3.82488 9.01038 3.74682 8.75591 3.74682C8.50144 3.74682 8.25309 3.82488 8.04438 3.97046C7.83566 4.11604 7.67663 4.32213 7.58872 4.56094L6.09497 8.59375L2.06216 10.0781C1.82336 10.166 1.61726 10.3251 1.47168 10.5338C1.3261 10.7425 1.24805 10.9908 1.24805 11.2453C1.24805 11.4998 1.3261 11.7481 1.47168 11.9568C1.61726 12.1656 1.82336 12.3246 2.06216 12.4125L6.09497 13.9062L7.57935 17.9391C7.66725 18.1779 7.82629 18.384 8.035 18.5295C8.24372 18.6751 8.49207 18.7532 8.74654 18.7532C9.00101 18.7532 9.24935 18.6751 9.45807 18.5295C9.66678 18.384 9.82582 18.1779 9.91372 17.9391L11.4075 13.9062L15.4403 12.4219C15.6791 12.334 15.8852 12.1749 16.0308 11.9662C16.1763 11.7575 16.2544 11.5092 16.2544 11.2547C16.2544 11.0002 16.1763 10.7519 16.0308 10.5432C15.8852 10.3344 15.6791 10.1754 15.4403 10.0875L15.4372 10.0828Z" fill="#148EFF" />
            </svg>
            <p className="text-base font-semibold text-primaryBlack">Still Doing Traditional Search?</p>
          </div>
          <p className="text-sm text-primaryBlack/50">
            Find qualified candidates with Northstarz assisted sourcing.
          </p>
        </div>
        <button
          onClick={handleContact}
          disabled={sending || sent}
          className="whitespace-nowrap text-white font-medium text-sm px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-60"
        >
          {sent ? "Request Submitted \u2014 Our Team Will Reach Out Shortly" : sending ? "Sending..." : "Unlock Sia"}
        </button>
      </div>
    </div>
  );
};



const JOB_STATUS_OPTIONS = [
  { label: "All Jobs", value: "all" },
  { label: "Open", value: "1" },
  { label: "Closed", value: "0" },
];

const OutreachedJobs = () => {
  const dispatch = useDispatch();
  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [filters, setFilters] = useState({
    jobTitle: "",
    department: "",
    sortBy: "",
  });

  const onSort = (key) => {
    if (filters.sortBy === key) setFilters({ ...filters, sortBy: "" });
    else
      setFilters({
        ...filters,
        sortBy: key,
      });
  };

  const columns = [
    {
      key: "jobTitle",
      header: "Outreach Jobs",
      notCentered: true,
      body: (row) => {
        return (
          <TableRedirectionLink
            redirectTo={REDIRECTION_LINKS.outreachedJob}
            state={{ id: row.id }}
          >
            <div className="flex flex-col gap-2 justify-start p-2">
              <p className="text-start text-sm text-web-blue font-semibold">
                {row.title}
              </p>
              {/* <p className="text-start text-xs text-primaryBlack">{row.field}</p> */}
            </div>
          </TableRedirectionLink>
        );
      },
    },
    {
      key: "outreach",
      header: "Total Outreach",
      body: (row) => {
        return (
          <p className="text-sm">
            {row.candidate_status_counts?.outreach || 0}
          </p>
        );
      },
    },
    {
      key: "applied",
      header: "Applied",
      body: (row) => {
        return (
          <p className="text-sm">
            {row.candidate_status_counts?.applied || 0}
          </p>
        );
      },
    },
    {
      key: "department",
      header: "Department",
      sortable: true,
      sortFn: () => onSort("department"),
      body: (row) => {
        return (
          <div className="text-sm">
            <p>{row.department}</p>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Open Date",
      sortable: true,
      sortFn: () => onSort("createdDate"),
      body: (row) => {
        return (
          <div className="text-sm">
            <p>{formatDateToShortMonth(row.createdDate)}</p>
          </div>
        );
      },
    },
    {
      key: "recruiter",
      header: "Recruiter",
      body: (row) => {
        return (
          <div className="text-sm">
            <p>{row.recruiter}</p>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      body: (row) => {
        const status = row.status;
        return (
          <div className="text-sm mx-auto w-fit">
            <Tag
              variant={STATUS_TO_TYPE_MAPPER[status].variant}
              text={STATUS_TO_TYPE_MAPPER[status].type}
            />
          </div>
        );
      },
    },

    {
      key: "actions",
      header: "Actions",
      body: (row) => <OutReachedAction jid={row.id} status={row.status} jobstatus={row.jobstatus} recruiterUuid={recruiterUuid} companyId={cid} />
    },
  ];

  const { page, page_size } = useSelector((state) =>
    selectCustomPagination(state, "outreachedJobs")
  );

  const cid = useSelector(selectCid);
  const recruiterUuid = useSelector(selectUuid);
  const specialTab = useSelector(getSpecialTab);

  const statusParam = jobStatusFilter && jobStatusFilter !== "all" ? "&status=" + jobStatusFilter : "";
  const jobsUrl = "/api/v1/jobs/" + cid + "?recruiter=" + recruiterUuid + statusParam;

  const { data: outreachedJobs = [], isLoading } = useFetch(jobsUrl);

  const statusCountsUrl = "/api/v1/jobs/" + cid + "/status-counts?recruiter=" + recruiterUuid + statusParam;

  const { data: statusCountsData, isLoading: isLoadingStatusCounts } = useFetch(statusCountsUrl);

  const { data: companyData } = useFetch(
    "/v2/company/get/" + cid
  );

  const departments = companyData?.departments || [];

  // Transform API data to match StatusCards format
  const cardsData = useMemo(() => {
    if (!statusCountsData?.candidate_status_counts) {
      return DUMMY_CARD_DATA;
    }

    const { outreach, applied, interviewed, shortlisted } = statusCountsData.candidate_status_counts;

    // Calculate percentages based on outreach count
    const calculatePercentage = (count) => {
      if (outreach === 0) return 0;
      return Math.round((count / outreach) * 100);
    };

    return [
      {
        title: "Total Outreach",
        count: outreach,
      },
      {
        title: "Applied",
        count: applied,
        percentage: calculatePercentage(applied),
      },
      {
        title: "AI Interviewed",
        count: interviewed,
        percentage: calculatePercentage(interviewed),
      },
      {
        title: "Shortlisted",
        count: shortlisted,
        percentage: calculatePercentage(shortlisted),
      },
    ];
  }, [statusCountsData]);

  const filteredJobs = outreachedJobs?.jobs
    ?.filter((job) => {
      return (
        job.title.toLowerCase().includes(filters.jobTitle.toLowerCase()) &&
        job.department.toLowerCase().includes(filters.department.toLowerCase())
      );
    })
    .sort((a, b) => {
      if (!filters.sortBy) return 0;

      const aVal = a[filters.sortBy];
      const bVal = b[filters.sortBy];

      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "string" && typeof bVal === "string") {
        const comparison = aVal.localeCompare(bVal, undefined, {
          sensitivity: "base",
        });
        return filters.sortOrder === "asc" ? comparison : -comparison;
      }

      return filters.sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    }) || [];

  const data = filteredJobs.slice(
    (page - 1) * page_size,
    (page - 1) * page_size + page_size
  );

  if (!outreachedJobs?.jobs?.length && !isLoading) {
    return (
      <EmptyScreen>
        <div className="flex flex-col items-center">
          <div className="flex flex-col items-center gap-2">
            <p className="text-3xl font-semibold text-primaryBlack">
              No Outreach started
            </p>
            <p className="text-sm text-primaryBlack/50">
              Start outreach for already created job posts.
            </p>
          </div>
          <Button variant="secondary" className="mt-4" onClick={() => { }}>
            Start Outreaching
          </Button>
        </div>
      </EmptyScreen>
    );
  }

  return (
    <>
      <PageLayout title={<div>Outreach Jobs</div>} className={"px-0"}>
        {!specialTab && <UpgradeBanner />}
        {isLoadingStatusCounts ? (
          <StatusCardsSkeleton count={4} />
        ) : (
          <StatusCards cardsData={cardsData} />
        )}
        <PageCard className={"mt-6 px-0"}>
          {isLoading ? (
            <div className="w-fit mx-auto">
              <TailSpin color="blue" height={80} width={80} />
            </div>
          ) : (
            <>
              <CustomTable
                tableData={data}
                columns={[...columns]}
                className={"!px-0"}
                Filters={
                  <div className="flex flex-row items-center gap-4">
                    <Input
                      placeholder={"Search Job Posts"}
                      className={"flex-1 w-96"}
                      Icon={<CiSearch className="h-5 w-5" />}
                      value={filters.jobTitle}
                      onChange={(e) => {
                        setFilters({ ...filters, jobTitle: e.target.value });
                        dispatch(
                          setCustomPagination({
                            page: 1,
                            page_size: page_size,
                            key: "outreachedJobs",
                          })
                        );
                      }}
                    />
                    <Select
                      placeholder="Select Department"
                      value={filters.department}
                      onChange={(e) => {
                        setFilters({ ...filters, department: e.target.value });
                        dispatch(
                          setCustomPagination({
                            page: 1,
                            page_size: page_size,
                            key: "outreachedJobs",
                          })
                        );
                      }}
                      options={departments.map((dept) => ({
                        value: dept,
                        label: dept,
                      }))}
                      className={"max-w-xs"}
                    />
                    <Select
                      placeholder="Select Job Status"
                      value={jobStatusFilter}
                      onChange={(e) => {
                        setJobStatusFilter(e.target.value);
                        dispatch(
                          setCustomPagination({
                            page: 1,
                            page_size: page_size,
                            key: "outreachedJobs",
                          })
                        );
                      }}
                      options={JOB_STATUS_OPTIONS}
                      className={"max-w-xs"}
                    />
                  </div>
                }
              />
              <Pagination
                pageKey={"outreachedJobs"}
                totalPages={Math.ceil(filteredJobs.length / page_size)}
              />
            </>
          )}
        </PageCard>
        <Copyright />
      </PageLayout>
    </>
  );
};

export default OutreachedJobs;
